import React, { useContext } from 'react';
import _ from 'lodash';
import { type RestMultiRowSurveyCard } from '@bbkAdminUtils/api-client/rest-cards-types';
import { type RestExperience } from '@bbkAdminUtils/api-client/rest-experiences-api';
import {
  RestModalAction,
  type RestOfferCardPack,
} from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import {
  type ModalActionHandler,
  type ModalOnClose,
} from '@bbkFunnel/components/modal';
import {
  type EventObject,
  type EventSyncObject,
  type ExecuteEventsHandler,
  type NextStepHandler,
} from '@bbkFunnel/events';
import { getModalCtaIdMap } from '@bbkFunnel/utils/cards-utils';
import DesignElement from '@bbkFunnelComponents/design-element/design-element';
import { EmptyOfferCardpack } from '@bbkFunnelComponents/design-element/empty-offer-cardpack';
import { FunnelContext } from '@bbkFunnelComponents/FunnelContext';
import { OfferCardPack } from '@bbkFunnelComponents/modals';

type Props = {
  card: RestOfferCardPack;
  syncData: {
    session: string;
    version: string;
    app_id: string | null;
    survey_state: RestMultiRowSurveyCard['provides'];
  };
  executeEvents: ExecuteEventsHandler;
  nextStep: NextStepHandler;
  continueAction: () => void;
  fullInfo: Pick<RestExperience, 'session' | 'version' | 'layout'>;
  nevermind: ModalOnClose;
};

export const OfferCardpackRenderer: React.FC<Props> = (props) => {
  const {
    fullInfo: {
      session,
      layout: { cards },
    },
    syncData,
    card,
    fullInfo,
    nextStep,
    executeEvents,
    continueAction,
    nevermind,
  } = props;

  const { isDesigner } = useContext(FunnelContext);

  const handleAction: ModalActionHandler = async (action, extra) => {
    const modalCtaIdMap = getModalCtaIdMap(cards);
    if (action.cta_details_list?.length) {
      if (!action.cta_details_list[0]?.cta_id) {
        GlobalLogger.error(
          `Offer card pack action (Navigation) exists with a non-zero cta_detail_list, but the ctaID of the first item is either blank or invalid. ${session}`
        );
      } else if (!action.cta_details_list?.[0]?.cta_id) {
        GlobalLogger.error(
          `Offer card pack action (Navigation) exists with a non-zero cta_detail_list and ctaID but the ?.[].? operator is not working for some browsers. ${session}`
        );
      }
    } else {
      GlobalLogger.log(
        `Offer card pack action (Navigation) is null or empty. ${session}`
      );
    }

    const formData = {
      sync: action.sync,
      message: _.get(action, 'sync.message'),
      survey_state: syncData.survey_state,
    };

    const commonKeys = {
      ..._.omit(syncData, 'survey_state'),
      card_version: card.version,
      direction: 'forwards' as 'forwards',
      id: card.id || null,
      name: card.name || card.id || 'offer_card_pack',
      cta_ids: action.cta_details_list?.map((cta) => cta.cta_id) ?? [
        modalCtaIdMap[card.name],
      ],
      cta_id: action.cta_details_list?.[0]?.cta_id ?? modalCtaIdMap[card.name],
    };

    const commonEvent: EventObject = {
      ...commonKeys,
      id: card.id || null,
      name: card.name || card.id || 'offer_card_pack',
      event: RestModalAction[action.action],
      url: action.url,
      properties: {
        survey_state: syncData.survey_state,
      },
    };

    const syncObj: EventSyncObject = {
      ...commonKeys,
      action: action.action,
      type: action.type,
      modal: action.modal,
      validation: action.validation,
      url: action.url,
      form: formData,
      survey_state: syncData.survey_state,
    };

    const acceptOfferEvent = {
      ...commonEvent,
      event: RestModalAction.accept_offer,
    };

    if (_.get(action, 'action') === 'send_email') {
      // Allow us to track the email message as well
      await executeEvents({
        synchronous: true,
        events: [acceptOfferEvent, commonEvent],
        syncs: [
          {
            ...syncObj,
            action: RestModalAction.accept_offer,
          },
          syncObj,
        ],
      });
      continueAction();
      nextStep(_.get(action, 'modal'));
      return;
    } else if (_.get(action, 'action') === 'accept_offer_with_confirmation') {
      executeEvents({
        synchronous: true,
        events: [acceptOfferEvent],
        syncs: [
          {
            ...syncObj,
            action: RestModalAction.accept_offer,
          },
        ],
      });
      nextStep(action.modal, _.get(action, 'initial_message', {}));
      continueAction();
    } else if (_.has(action, 'modal')) {
      nextStep(action.modal, _.get(action, 'initial_message', {}));
      continueAction();
    } else if (_.get(action, 'action') === RestModalAction.cancel_with_link) {
      executeEvents({
        synchronous: true,
        events: [
          {
            ...commonEvent,
            event: 'cancel',
          },
        ],
        syncs: [
          {
            ...syncObj,
            url: action.url,
            action: RestModalAction.link,
          },
        ],
      });
    } else if (_.get(action, 'action') === 'nevermind') {
      const eventObj = {
        session: syncData.session,
        version: syncData.version,
        app_id: syncData.app_id,
        id: card.id,
        name: card.name,
        event: 'nevermind',
        properties: {
          survey_state: syncData.survey_state,
        },
      };

      const syncObj: EventSyncObject = {
        session: syncData.session,
        version: syncData.version,
        app_id: syncData.app_id,
        id: card.id,
        name: card.name,
        direction: 'backwards',
        type: action.type,
        action: RestModalAction.link,
        url: action.url,
        validation: action.validation,
        form: action.sync,
        survey_state: syncData.survey_state,
      };

      executeEvents({
        synchronous: true,
        events: [eventObj],
        syncs: [syncObj],
      });
      return;
    } else {
      executeEvents({
        synchronous: true,
        events: [acceptOfferEvent, commonEvent],
        syncs: [
          {
            ...syncObj,
            action: RestModalAction.accept_offer,
          },
          syncObj,
        ],
      });
      return;
    }
  };

  return (
    <div className="bbk-offer-set tw-relative">
      <DesignElement name="Offer card pack" noCard={!card}>
        {!_.isEmpty(card) ? (
          <OfferCardPack
            modalInfo={card}
            close={() => {}}
            handleAction={handleAction}
            isPreview
          />
        ) : isDesigner ? (
          <EmptyOfferCardpack />
        ) : (
          <div className="tw-w-full" />
        )}
      </DesignElement>
    </div>
  );
};
